import { formatCount } from './format';
import { planSlotSend } from './quota';
import type { AccountCapacity } from './types';

export interface ConsumptionReview {
  estimated: number;
  remainingAfter: number;
  exceedsAvailable: boolean;
  exceedsCompany: boolean;
  exceedsCampaign: boolean;
  canApprove: boolean;
  blocks: string[];
  unitsPerPerson: number;
  /** Pessoas que cabem na cota restante, respeitando os limites internos. */
  sendCount: number;
  slotsToReserve: number;
  leftOut: number;
  canSendPartial: boolean;
  quotaEmpty: boolean;
}

export interface ConsumptionOptions {
  /** Unidades por pessoa: partes do SMS; sempre 1 no e-mail. */
  unitsPerPerson?: number;
  /** Unidade no plural, para as mensagens de bloqueio ("e-mails", "SMS"). */
  unit: string;
  /** Bloqueio quando não há unidade por pessoa — o SMS sem texto escrito. */
  missingUnitsMessage?: string;
}

/**
 * Estimativa de UI. Não consulta saldo e não reserva nada de verdade: o
 * backend refaz esta conta e reserva de forma atômica antes de liberar o envio.
 */
export function reviewConsumption(
  eligible: number,
  capacity: AccountCapacity,
  { unitsPerPerson = 1, unit, missingUnitsMessage }: ConsumptionOptions,
): ConsumptionReview {
  const people = Math.max(0, eligible);
  const parts = Math.max(0, unitsPerPerson);
  const blocks: string[] = [];
  if (people <= 0) blocks.push('Não há público elegível para esta campanha.');
  if (parts <= 0 && missingUnitsMessage) blocks.push(missingUnitsMessage);

  const estimated = people > 0 && parts > 0 ? people * parts : 0;
  const exceedsAvailable = estimated > capacity.available;
  const exceedsCompany = estimated > capacity.companyLimit;
  const exceedsCampaign = estimated > capacity.campaignLimit;
  if (estimated > 0 && exceedsCampaign) {
    blocks.push(`Passa do limite interno da campanha (${formatCount(capacity.campaignLimit)} ${unit}).`);
  }
  if (estimated > 0 && exceedsCompany) {
    blocks.push(`Passa do limite interno da empresa (${formatCount(capacity.companyLimit)} ${unit}).`);
  }
  if (estimated > 0 && exceedsAvailable) {
    blocks.push(`Passa dos ${formatCount(capacity.available)} ${unit} disponíveis nesta demonstração.`);
  }

  const plan = planSlotSend(people, parts, capacity);
  return {
    estimated,
    remainingAfter: capacity.available - estimated,
    exceedsAvailable,
    exceedsCompany,
    exceedsCampaign,
    canApprove: blocks.length === 0,
    blocks,
    unitsPerPerson: parts,
    sendCount: plan.sendPeople,
    slotsToReserve: plan.slotsToReserve,
    leftOut: plan.leftOut,
    canSendPartial: plan.canSendPartial,
    quotaEmpty: plan.quotaEmpty,
  };
}
